"use client";

import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { type infer as ZodInfer } from "zod";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Trash2, ClipboardList, Plus } from "lucide-react";

import { ComboSearch } from "@/components/ComboSearch";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import CurrencyInput from "@/components/CurrencyInput";
import { ProductsService } from "@/services/ProductsService";
import { FastOrderService } from "@/services/FastOrderService";
import type { ProductsOutput } from "@/interfaces";
import { DatePickerComponent } from "@/components/DatePickerComponent";
import { schema } from "./schema/schema";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { localDate } from "@/util/dates";
import { numberToMoney } from "@/util/moneyConversion";

type FastOrderFormValues = ZodInfer<typeof schema>;
type FastOrderProductRow = { productId: string; quantity: number };
type FastOrderProductRows = FastOrderProductRow[];

export default function FastOrderForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;

  const productService = new ProductsService();
  const fastOrderService = new FastOrderService();

  const [products, setProducts] = useState<ProductsOutput[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);

  const form = useForm<FastOrderFormValues, undefined, FastOrderFormValues>({
    resolver: zodResolver(schema) as Resolver<
      FastOrderFormValues,
      undefined,
      FastOrderFormValues
    >,
    defaultValues: {
      orderDate: localDate(),
      products: [],
      discount: 0,
      description: "",
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const { data: fetchedProducts } = await productService.findAll({
          pageSize: 100,
        });
        setProducts(Array.isArray(fetchedProducts) ? fetchedProducts : []);

        if (isEditing && id) {
          try {
            const orderDetails = await fastOrderService.findById(id);
            form.reset({
              ...orderDetails,
              orderDate: orderDetails.orderDate
                ? new Date(orderDetails.orderDate)
                : localDate(),
              products:
                Array.isArray(orderDetails.products) &&
                orderDetails.products.length > 0
                  ? orderDetails.products.map((p) => ({
                      productId: p.productId || "",
                      quantity: p.quantity || 1,
                    }))
                  : [],
              discount: orderDetails.discount || 0,
              description: orderDetails.description || "",
            });
          } catch (error) {
            console.error("Erro ao buscar detalhes do pedido:", error);
            navigate("/pedidos-rapidos");
          }
        }
        setDataLoaded(true);
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [id, form, navigate, isEditing]);

  const watchProductsRaw = form.watch("products");
  const watchProducts: FastOrderProductRows = Array.isArray(watchProductsRaw)
    ? watchProductsRaw
    : [];
  const watchDiscount = form.watch("discount") ?? 0;

  const calculateTotalValue = () => {
    if (!Array.isArray(products) || products.length === 0) return "R$ 0,00";

    const subtotal = watchProducts
      .map((productRow: FastOrderProductRow) => {
        const product = products?.find(
          (productItem) => productItem.id === productRow.productId
        );
        return (product?.unitPrice ?? 0) * (productRow.quantity ?? 0);
      })
      .reduce<number>((sum, value) => sum + value, 0);

    return numberToMoney(subtotal - watchDiscount);
  };

  const addProductRow = () => {
    form.setValue("products", [
      ...watchProducts,
      { productId: "", quantity: 1 },
    ] as FastOrderProductRows);
  };

  const removeProductRow = (index: number) => {
    const updatedProducts = watchProducts.filter(
      (_product: FastOrderProductRow, currentIndex: number) =>
        currentIndex !== index
    );
    form.setValue("products", updatedProducts as FastOrderProductRows);
  };
  const onSubmit = async (data: FastOrderFormValues) => {
    try {
      setIsLoading(true);
      let fastOrderId = id;
      if (isEditing && id) {
        await fastOrderService.update(id, data);
      } else {
        const response = await fastOrderService.create(data);
        fastOrderId = response.id;
      }
      navigate(`/pedidos-rapidos/${fastOrderId}`);
    } catch (error) {
      console.error("Erro ao salvar pedido rápido:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditing && !dataLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card className="overflow-hidden border-none shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
              <div className="flex items-center gap-3">
                <ClipboardList className="h-6 w-6" />
                <CardTitle className="text-xl">
                  {isEditing ? "Editar Pedido Rápido" : "Novo Pedido Rápido"}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4">
              <FormField
                control={form.control}
                name="orderDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Data do pedido</FormLabel>
                    <FormControl>
                      <DatePickerComponent
                        {...field}
                        id="orderDate"
                        selected={field.value}
                        onSelect={(date) => field.onChange(date || new Date())}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Products Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FormLabel>Produtos</FormLabel>
                  <Button
                    type="button"
                    onClick={addProductRow}
                    variant="outline"
                    size="sm"
                    className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Produto
                  </Button>
                </div>

                <div className="space-y-3 border rounded-lg p-3 bg-gray-50">
                  {watchProducts.map(
                    (_product: FastOrderProductRow, index: number) => (
                      <div
                        key={index}
                        className="flex justify-start gap-2 items-start"
                      >
                        <FormField
                          control={form.control}
                          name={`products.${index}.productId`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormControl className="flex-grow">
                                <ComboSearch
                                  id={`product-${index}`}
                                  context="product"
                                  value={field.value || ""}
                                  onSelect={(value) => field.onChange(value)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name={`products.${index}.quantity`}
                          render={({ field }) => (
                            <FormItem className="w-24">
                              <FormControl>
                                <Input
                                  type="number"
                                  min="1"
                                  className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] text-center"
                                  value={field.value}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeProductRow(index)}
                          className="h-10 w-10 border-red-500 text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  )}
                </div>
              </div>

              <FormField
                control={form.control}
                name="discount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Desconto</FormLabel>
                    <FormControl>
                      <CurrencyInput
                        {...field}
                        className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição do pedido</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Descrição do pedido"
                        {...field}
                        className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Total Value */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <FormLabel className="m-0">Valor Total</FormLabel>
                  <div className="text-lg font-bold text-[#EE7B11]">
                    {calculateTotalValue()}
                  </div>
                </div>
              </div>

              <Separator className="my-2" />
            </CardContent>
            <CardFooter className="border-t p-4 sm:p-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/pedidos-rapidos")}
                className="border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                {isEditing ? "Atualizar" : "Salvar"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}
